import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { mget, mput, mdel, errMsg, fmtDate, SOURCE_LABEL } from "@/manager/lib";
import { KindPill, StatusPill, Card, goldBtn } from "@/manager/ui";
import EventFormDialog, { toPayload } from "@/manager/components/EventFormDialog";
import SummaryTab from "@/manager/event/SummaryTab";
import ServicesTab from "@/manager/event/ServicesTab";
import StaffTab from "@/manager/event/StaffTab";
import TaskList from "@/manager/components/TaskList";
import ConsumptionTab from "@/manager/event/ConsumptionTab";
import FinanceTab from "@/manager/event/FinanceTab";
import DocumentsTab from "@/manager/event/DocumentsTab";

const TABS = [["resumen", "Resumen"], ["servicios", "Servicios"], ["personal", "Personal"], ["tareas", "Tareas"],
  ["consumos", "Inventario y consumos"], ["finanzas", "Finanzas"], ["documentos", "Documentos"], ["notas", "Notas"]];

function NotesTab({ ev, save }) {
  const [notes, setNotes] = useState(ev.notes || "");
  return (
    <Card className="p-5 space-y-3">
      <Textarea rows={8} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observaciones internas, incidencias, enlaces a documentos…" data-testid="ev-notes" />
      <Button className={goldBtn} onClick={() => save({ notes })} data-testid="ev-notes-save">Guardar notas</Button>
    </Card>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [ev, setEv] = useState(null);
  const [edit, setEdit] = useState(false);
  const tab = params.get("tab") || "resumen";

  const load = useCallback(() => mget(`/events/${id}`).then(setEv).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, msg = "Guardado") => {
    try { setEv(await mput(`/events/${id}`, toPayload({ ...ev, ...patch }))); toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar este evento?")) return;
    try { await mdel(`/events/${id}`); toast.success("Evento eliminado"); navigate("/manager/eventos"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!ev) return <p className="text-sm text-[#6b6a66]">Cargando…</p>;

  return (
    <div data-testid="manager-event-detail">
      <button onClick={() => navigate(-1)} className="text-xs text-[#6b6a66] flex items-center gap-1 mb-4 hover:text-[#101B28]" data-testid="ev-back"><ArrowLeft size={14} /> Volver</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2"><KindPill kind={ev.kind} /><StatusPill status={ev.status} />
            <span className="text-[11px] text-[#6b6a66]">{SOURCE_LABEL[ev.external_source] || "Creado en Manager"}</span></div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight" data-testid="ev-title">{ev.title}</h1>
          <p className="text-sm text-[#6b6a66] mt-1">{fmtDate(ev.date)} · {ev.start_time || "--:--"}–{ev.end_time || "--:--"} · {ev.location || "Sin ubicación"} · {ev.guests ?? "?"} invitados</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full gap-2" onClick={() => setEdit(true)} data-testid="ev-edit"><Pencil size={14} /> Editar datos</Button>
          {!ev.external_source && <Button variant="outline" className="rounded-full text-rose-700" onClick={remove} data-testid="ev-delete"><Trash2 size={14} /></Button>}
        </div>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="h-auto flex-wrap justify-start bg-white border border-[#e6e1d6] rounded-2xl p-1 mb-6">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`ev-tab-${k}`} className="rounded-xl text-xs data-[state=active]:bg-[#101B28] data-[state=active]:text-white">{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="resumen"><SummaryTab ev={ev} save={save} goTab={(t) => setParams({ tab: t })} /></TabsContent>
        <TabsContent value="servicios"><ServicesTab ev={ev} save={save} /></TabsContent>
        <TabsContent value="personal"><StaffTab ev={ev} save={save} reload={load} /></TabsContent>
        <TabsContent value="tareas"><TaskList tasks={ev.tasks} reload={load} eventId={ev.id} /></TabsContent>
        <TabsContent value="consumos"><ConsumptionTab ev={ev} reload={load} /></TabsContent>
        <TabsContent value="finanzas"><FinanceTab ev={ev} save={save} reload={load} /></TabsContent>
        <TabsContent value="documentos"><DocumentsTab ev={ev} /></TabsContent>
        <TabsContent value="notas"><NotesTab ev={ev} save={save} /></TabsContent>
      </Tabs>
      <EventFormDialog open={edit} event={ev} onClose={() => setEdit(false)} onSaved={() => load()} />
    </div>
  );
}
